"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

export type StatRowItem = {
  icon?: LucideIcon;
  value: React.ReactNode;
  label: string;
};

export type StatRowProps = {
  stats: StatRowItem[];
  /** Strip border, background and shadow — use when StatRow lives inside a card container. */
  bare?: boolean;
  /** Stack items vertically instead of horizontally. Uses the same muted small text as reports-list stats. */
  direction?: "horizontal" | "vertical";
  className?: string;
};

/**
 * Compact stat strip. Replaces the card-grid pattern with slim `icon · value · label` pairs.
 * Use `direction="vertical"` to stack items as rows (reports-list style muted text).
 */
export function StatRow({ stats, bare = false, direction = "horizontal", className }: StatRowProps) {
  if (direction === "vertical") {
    return (
      <div className={cn("flex flex-col gap-y-1", className)}>
        {stats.map((stat) => (
          <span key={stat.label} className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
            {stat.icon && <stat.icon className="h-3 w-3 shrink-0" aria-hidden="true" />}
            <span className="tabular-nums">{stat.value}</span>
            <span>{stat.label}</span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1",
        bare
          ? "p-0"
          : "px-3 py-1.5 border border-border bg-card w-full sm:w-auto sm:inline-flex",
        className,
      )}
      style={bare ? undefined : { boxShadow: "var(--shadow-warm-sm)" }}
    >
      {stats.map((stat, i) => (
        <React.Fragment key={stat.label}>
          {i > 0 && (
            <span aria-hidden="true" className="text-border/60 select-none text-[10px]">
              ·
            </span>
          )}
          <div className="flex items-center gap-1">
            {stat.icon && (
              <stat.icon
                className="h-2.5 w-2.5 shrink-0 text-primary"
                aria-hidden="true"
              />
            )}
            <span className="text-[13px] font-bold tabular-nums text-foreground leading-none">
              {stat.value}
            </span>
            <span className="text-[10px] font-medium text-muted-foreground leading-none">
              {stat.label}
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
